import { parseISO } from 'date-fns';
import {
  floorsOf,
  mondayOf,
  openSpan,
  PLAN_STATUSES,
  rungOf,
  windowBounds,
  zoneFloors,
  type IPromiseFacts,
  type PlanWeeks,
} from '@d-orca/shared/client/activities';
import type { ActivityGroupStatus, IScheduleProfession, ISchedulerTask, ISiteFloor } from '@interfaces/IScheduler';
import { restDayKind, type IRestDayCol } from '../../SchedulerPage/lib/calendarGrid';
import type { LadderRung } from '../../SchedulerPage/lib/ladder';
import { addDays, daysBetween } from '../../SchedulerPage/lib/timeScale';
import { windowOverlaps } from '../activitiesFilter';
import { DEFAULT_HIDDEN_STATUSES, ISSUE_STATUS_KEYS } from '../pageFilters';
import type { IListItem } from '../model/listItem';

// ---------------------------------------------------------------------------
// The Work Plan view's layout maths (docs/specs/2026-09-22-work-plan-view-design.md).
// Pure: every day boundary, bar, overrun, group, issue bucket and strip count
// is decided here, over `YYYY-MM-DD` site days; the components only render it.
//
// Rows are PROMISES — committed activities of the feed — and each wears the
// one status ladder (`ladderOf`). An overdue promise holds the board until
// today (decision 5): nothing leaves the plan by being late. What a promise
// is on its own — its rung, lateness, span and facts, and the page's window —
// is shared with the mobile list (`@d-orca/shared/client/activities`).
// ---------------------------------------------------------------------------

export {
  inWindow,
  itemStatusKey,
  lateDaysOf,
  mondayOf,
  openSpan,
  PLAN_STATUSES,
  PLAN_WEEKS,
  promiseFacts,
  rungOf,
  zoneFloors,
} from '@d-orca/shared/client/activities';
export type { IPromiseFacts, PlanWeeks } from '@d-orca/shared/client/activities';

export type PlanGroupBy = 'profession' | 'floor' | 'assignee' | 'none';
export const PLAN_GROUPINGS: readonly PlanGroupBy[] = ['profession', 'floor', 'assignee', 'none'];

const isRung = (status: string): boolean => PLAN_STATUSES.some((rung) => rung === status);

/** The one rung the Status checkboxes show, when they show exactly one —
 *  the strip tile that reads as pressed. */
export function soleRung(hidden: readonly string[]): LadderRung | null {
  const shown = PLAN_STATUSES.filter((rung) => !hidden.includes(rung));
  return shown.length === 1 ? shown[0]! : null;
}

/** The rungs the Status checkboxes show out of the box. */
const DEFAULT_RUNGS = PLAN_STATUSES.filter((rung) => !DEFAULT_HIDDEN_STATUSES.includes(rung));

/** The Status checkboxes focused on `rungs` alone, with the issues' boxes at
 *  their default or all unchecked; the tracker's statuses left as they are. */
function focus(hidden: readonly string[], rungs: readonly LadderRung[], withIssues: boolean): string[] {
  const kept = hidden.filter((status) => !isRung(status) && !ISSUE_STATUS_KEYS.includes(status));
  const issues = withIssues ? ISSUE_STATUS_KEYS.filter((key) => DEFAULT_HIDDEN_STATUSES.includes(key)) : ISSUE_STATUS_KEYS;
  return [...kept, ...PLAN_STATUSES.filter((rung) => !rungs.includes(rung)), ...issues];
}

/** What a summary rung writes: its rung alone, the issues out — or, pressed
 *  again, the rungs and the issues back to their default. */
export function toggleSoleRung(hidden: readonly string[], rung: LadderRung): string[] {
  return soleRung(hidden) === rung ? focus(hidden, DEFAULT_RUNGS, true) : focus(hidden, [rung], false);
}

/** Every rung unchecked: the grid holds the issue lane alone. */
export function hidesEveryRung(hidden: readonly string[]): boolean {
  return PLAN_STATUSES.every((rung) => hidden.includes(rung));
}

/** What the summary's issues write: every rung unchecked, the issues at
 *  their default — or, pressed again, the rungs back too. */
export function toggleIssuesOnly(hidden: readonly string[]): string[] {
  return focus(hidden, hidesEveryRung(hidden) ? DEFAULT_RUNGS : [], true);
}

/** Labels (trade names, floor order, usernames) sort naturally in the
 *  product's locale — "2F" before "10F". */
const COLLATOR = new Intl.Collator('ja', { numeric: true });

const later = (a: string, b: string): string => (a > b ? a : b);
const earlier = (a: string, b: string): string => (a < b ? a : b);

// ---------------------------------------------------------------------------
// The window
// ---------------------------------------------------------------------------

/** A rest day's kind, as the Gantt colours it: Saturday blue, Sunday red,
 *  any other rest weekday neutral. */
export type RestTone = IRestDayCol['kind'];

export interface IDayCol {
  date: string;
  /** Null on a working day. */
  rest: RestTone | null;
  isToday: boolean;
}

/** One ISO week of the window: the header's band. */
export interface IWeekBand {
  monday: string;
  sunday: string;
}

/** One calendar month's stretch of the window: the header's top band. */
export interface IMonthBand {
  /** The month's first day (`YYYY-MM-01`), whether or not the window holds it. */
  month: string;
  col: number;
  span: number;
  /** The month today falls in — the Gantt highlights it. */
  current: boolean;
}

export interface IPlanWindow {
  from: string;
  to: string;
  days: IDayCol[];
  weeks: IWeekBand[];
  months: IMonthBand[];
}

/** The window's days grouped by calendar month, in column terms. */
function monthBandsOf(days: readonly IDayCol[], today: string): IMonthBand[] {
  const bands: IMonthBand[] = [];
  days.forEach((day, i) => {
    const month = `${day.date.slice(0, 7)}-01`;
    const band = bands.at(-1);
    if (band?.month === month) band.span += 1;
    else bands.push({ month, col: i + 1, span: 1, current: month.slice(0, 7) === today.slice(0, 7) });
  });
  return bands;
}

/** The page's window (`windowBounds`: whole weeks from a Monday) laid out as
 *  the grid's columns and header bands; rest days only shade. */
export function windowFor(today: string, weeks: PlanWeeks, offsetWeeks: number, restDays: ReadonlySet<number>): IPlanWindow {
  const { from, to } = windowBounds(today, weeks, offsetWeeks);
  const days = Array.from({ length: weeks * 7 }, (_, i) => {
    const date = addDays(from, i);
    // Rest days shade as the Gantt shades them (its own rule).
    return { date, rest: restDayKind(parseISO(date).getDay(), restDays), isToday: date === today };
  });
  const bands = Array.from({ length: weeks }, (_, w) => ({
    monday: addDays(from, w * 7),
    sunday: addDays(from, w * 7 + 6),
  }));
  return { from, to, days, weeks: bands, months: monthBandsOf(days, today) };
}

// ---------------------------------------------------------------------------
// Promise rows
// ---------------------------------------------------------------------------

export interface IPromiseRow {
  /** The list item key (`act:<taskId>`). */
  key: string;
  task: ISchedulerTask;
  rung: LadderRung;
  /** Trusstor's word for the activity — what the rail's ladder chip reads. */
  activityStatus: ActivityGroupStatus | null;
  /** The last day the promise holds on the board — its planned end, or today
   *  while it is overdue past that end (decision 5). */
  openEnd: string;
  /** Days past the planned end while overdue; 0 otherwise. */
  lateDays: number;
}

/** One row per committed activity item. */
export function promiseRows(items: readonly IListItem[], today: string): IPromiseRow[] {
  return items.map((item) => {
    const task = item.raw.activity!;
    const activityStatus = item.activityStatus ?? null;
    const rung = rungOf(item);
    const [, openEnd] = openSpan(task, rung, today);
    return { key: item.key, task, rung, activityStatus, openEnd, lateDays: daysBetween(task.end, openEnd) };
  });
}

/** The rows the grid shows: those holding a day of the window whose rung
 *  the Status checkboxes do not hide. */
export function visibleRows<T extends IPromiseRow>(rows: readonly T[], window: Pick<IPlanWindow, 'from' | 'to'>, hidden: readonly string[]): T[] {
  return rows.filter((row) => windowOverlaps(row.task.start, row.openEnd, [window.from, window.to]) && !hidden.includes(row.rung));
}

// ---------------------------------------------------------------------------
// Placement — grid columns, 1-based, clipped to the window
// ---------------------------------------------------------------------------

export interface IBarPlacement {
  col: number;
  span: number;
  /** The promise continues before / after the window. */
  cutL: boolean;
  cutR: boolean;
}

export interface IOverrunPlacement {
  col: number;
  span: number;
  /** The planned span lies wholly before the window: the overrun alone
   *  carries the name. */
  lone: boolean;
}

export interface IRowPlacement {
  bar: IBarPlacement | null;
  overrun: IOverrunPlacement | null;
}

export interface IPlacedRow extends IPromiseRow {
  placement: IRowPlacement;
  facts: IPromiseFacts;
}

function colOf(window: Pick<IPlanWindow, 'from'>, day: string): number {
  return daysBetween(window.from, day) + 1;
}

/**
 * Where a VISIBLE row's marks sit (`visibleRows` guarantees it holds a day of
 * the window, so it has a bar, an overrun, or both). The bar is the planned
 * span; an overdue promise adds the overrun from the day after its planned end
 * to today.
 */
export function placeRow(row: IPromiseRow, window: IPlanWindow): IRowPlacement {
  const { start, end } = row.task;
  const barFrom = later(start, window.from);
  const bar =
    end < window.from
      ? null
      : {
          col: colOf(window, barFrom),
          span: daysBetween(barFrom, earlier(end, window.to)) + 1,
          cutL: start < window.from,
          cutR: end > window.to,
        };
  const overFrom = later(addDays(end, 1), window.from);
  const overTo = earlier(row.openEnd, window.to);
  const overrun =
    row.lateDays > 0 && overFrom <= overTo
      ? { col: colOf(window, overFrom), span: daysBetween(overFrom, overTo) + 1, lone: bar === null }
      : null;
  return { bar, overrun };
}

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

export type GroupHeader =
  | { kind: 'profession'; profession: IScheduleProfession }
  | { kind: 'floor'; name: string }
  | { kind: 'assignee'; username: string }
  | { kind: 'noProfession' | 'noArea' | 'noAssignee' };

export interface IPromiseGroup<T extends IPromiseRow = IPromiseRow> {
  key: string;
  /** null for the flat list (Group by: None), which has no header row. */
  header: GroupHeader | null;
  rows: T[];
  /** The rows' mean progress, like a Gantt summary row. */
  progress: number;
  delayed: number;
  overdue: number;
}

export interface IGroupContext {
  floors: readonly ISiteFloor[];
  /** The display label a profession group sorts by. */
  professionLabel: (profession: IScheduleProfession) => string;
}

interface IBucket {
  key: string;
  header: GroupHeader;
  /** The "No …" bucket, which sorts after every named one. */
  last: boolean;
  /** Sort value among the named buckets. */
  order: string;
}

function bucketOf(task: ISchedulerTask, groupBy: Exclude<PlanGroupBy, 'none'>, ctx: IGroupContext, floorOfZone: ReadonlyMap<string, number>): IBucket {
  if (groupBy === 'profession') {
    const { profession } = task;
    return profession
      ? { key: `profession:${profession.tradeId}`, header: { kind: 'profession', profession }, last: false, order: ctx.professionLabel(profession) }
      : { key: 'noProfession', header: { kind: 'noProfession' }, last: true, order: '' };
  }
  if (groupBy === 'floor') {
    // An administrative task has no areas — it sits under "No area".
    const index = floorsOf(task, floorOfZone)[0];
    return index === undefined
      ? { key: 'noArea', header: { kind: 'noArea' }, last: true, order: '' }
      : { key: `floor:${ctx.floors[index]!.id}`, header: { kind: 'floor', name: ctx.floors[index]!.name }, last: false, order: String(index) };
  }
  return task.assignee
    ? { key: `assignee:${task.assignee}`, header: { kind: 'assignee', username: task.assignee }, last: false, order: task.assignee }
    : { key: 'noAssignee', header: { kind: 'noAssignee' }, last: true, order: '' };
}

const byStartThenName = (a: IPromiseRow, b: IPromiseRow): number =>
  COLLATOR.compare(a.task.start, b.task.start) || COLLATOR.compare(a.task.name, b.task.name);

function groupOf<T extends IPromiseRow>(key: string, header: GroupHeader | null, rows: T[]): IPromiseGroup<T> {
  return {
    key,
    header,
    rows: rows.sort(byStartThenName),
    progress: rows.length > 0 ? Math.round(rows.reduce((sum, row) => sum + row.task.progressPercent, 0) / rows.length) : 0,
    delayed: rows.filter((row) => row.rung === 'delayed').length,
    overdue: rows.filter((row) => row.rung === 'overdue').length,
  };
}

/** Rows into the chosen grouping: groups in label (or building) order with the
 *  "No …" group last, rows by start date then name. */
export function groupRows<T extends IPromiseRow>(rows: readonly T[], groupBy: PlanGroupBy, ctx: IGroupContext): IPromiseGroup<T>[] {
  if (groupBy === 'none') return [groupOf('all', null, [...rows])];
  const floorOfZone = zoneFloors(ctx.floors);
  const buckets = new Map<string, { bucket: IBucket; rows: T[] }>();
  for (const row of rows) {
    const bucket = bucketOf(row.task, groupBy, ctx, floorOfZone);
    const entry = buckets.get(bucket.key);
    if (entry) entry.rows.push(row);
    else buckets.set(bucket.key, { bucket, rows: [row] });
  }
  return [...buckets.values()]
    .sort((a, b) => Number(a.bucket.last) - Number(b.bucket.last) || COLLATOR.compare(a.bucket.order, b.bucket.order))
    .map(({ bucket, rows: members }) => groupOf(bucket.key, bucket.header, members));
}

// ---------------------------------------------------------------------------
// The issue lane
// ---------------------------------------------------------------------------

export interface IIssueChip {
  /** The list item key (`iss:<id>`) — what the issue drawer opens on. */
  key: string;
  title: string;
  /** The issue's review state (`planned`, `forReview`, `rejected`). */
  status: string;
  /** Due day, or null when the issue has none. */
  due: string | null;
  safety: boolean;
  critical: boolean;
  /** Past its due date and still open. */
  late: boolean;
}

export interface IIssueLane {
  byDay: ReadonlyMap<string, IIssueChip[]>;
  noDue: IIssueChip[];
  /** Unfinished issues whose due day passed before the window starts. */
  lateBefore: number;
}

/** The site's issues as lane chips, most urgent first: safety, then critical,
 *  then by title. Completed ones too — the Status checkboxes hide them by
 *  default, and show them on the lane when asked. */
export function planIssues(items: readonly IListItem[]): IIssueChip[] {
  return items
    .filter((item) => item.kind === 'issue')
    .map((item) => ({
      key: item.key,
      title: item.title,
      status: item.status,
      due: item.end ?? null,
      safety: item.isSafety,
      critical: item.isCritical,
      late: item.isOverdue,
    }))
    .sort(
      (a, b) =>
        Number(b.safety) - Number(a.safety) || Number(b.critical) - Number(a.critical) || COLLATOR.compare(a.title, b.title),
    );
}

/** Issues on their due day inside the window; the rest counted around it. */
export function issueLane(issues: readonly IIssueChip[], window: Pick<IPlanWindow, 'from' | 'to'>): IIssueLane {
  const byDay = new Map<string, IIssueChip[]>();
  const noDue: IIssueChip[] = [];
  let lateBefore = 0;
  for (const issue of issues) {
    if (issue.due === null) {
      noDue.push(issue);
      continue;
    }
    if (issue.due < window.from) {
      if (issue.late) lateBefore += 1;
      continue;
    }
    if (issue.due > window.to) continue;
    const day = byDay.get(issue.due);
    if (day) day.push(issue);
    else byDay.set(issue.due, [issue]);
  }
  return { byDay, noDue, lateBefore };
}

/** A window this long (4 weeks) draws compact: day numbers only, issue
 *  chips collapsed to markers. */
export function isCompact(window: Pick<IPlanWindow, 'days'>): boolean {
  return window.days.length > 14;
}

/** Chips a lane cell draws: two per day, one on a compact window. */
export function laneCap(window: Pick<IPlanWindow, 'days'>): number {
  return isCompact(window) ? 1 : 2;
}

export function capChips<T>(chips: readonly T[], cap: number): { shown: T[]; more: number } {
  return { shown: chips.slice(0, cap), more: Math.max(0, chips.length - cap) };
}

// ---------------------------------------------------------------------------
// The strip — the weekly review's numbers
// ---------------------------------------------------------------------------

export interface IPpc {
  done: number;
  total: number;
  percent: number;
}

/**
 * The Work Plan's one-line summary over the page's window: each rung in the
 * window whatever the Status checkboxes hide — one rung's filter never zeroes
 * its neighbours — and the issues the lane holds. PPC is last week's,
 * whatever the window.
 */
export interface ISummary {
  inProgress: number;
  delayed: number;
  overdue: number;
  /** Last week's Percent Plan Complete; null when nothing was due. */
  ppc: IPpc | null;
  issues: number;
}

/**
 * Percent Plan Complete over the Mon–Sun week starting `monday`: of the
 * promises whose PLANNED end fell in that week, the share that reached done or
 * areas done. Measured live, on the rung each has now — no history.
 *
 * The week is the whole Mon–Sun week, the review's unit (decision 9), not the
 * working week: a promise planned to end on a rest day still counts in the
 * week that day belongs to instead of falling between two working weeks.
 */
export function ppcFor(rows: readonly IPromiseRow[], monday: string): IPpc | null {
  const sunday = addDays(monday, 6);
  const due = rows.filter((row) => row.task.end >= monday && row.task.end <= sunday);
  if (due.length === 0) return null;
  const done = due.filter((row) => row.rung === 'done' || row.rung === 'areasDone').length;
  return { done, total: due.length, percent: Math.round((done / due.length) * 100) };
}

/** `issues`: the issues the lane holds (under their Status checkboxes). */
export function summaryFor(
  rows: readonly IPromiseRow[],
  window: Pick<IPlanWindow, 'from' | 'to'>,
  issues: readonly IIssueChip[],
  today: string,
): ISummary {
  const inWindow = visibleRows(rows, window, []);
  const count = (rung: LadderRung): number => inWindow.filter((row) => row.rung === rung).length;
  return {
    inProgress: count('inProgress'),
    delayed: count('delayed'),
    overdue: count('overdue'),
    ppc: ppcFor(rows, addDays(mondayOf(today), -7)),
    issues: issues.length,
  };
}
